/**
 * Tax "R" Us Clara Intelligence — Global Constants
 * Mirrors prototype's CONFIG and CLS objects with TypeScript types
 */

/**
 * APPLICATION CONFIGURATION CONSTANTS
 */
export const CONFIG = {
  // Main app IDs
  CLARA_APP: 'tru-clara-app',
  WORKSPACE: 'kci-workspaces',
  AGENTS: 'kci-agents',
  WORKFLOW: 'kc-workflow',
  WORKPAPER: 'tru-workpaper',
  INVENTORY: 'tru-inventory',
  TASKS: 'tru-tasks',
  QUALITY: 'tru-quality',
  VAULT: 'tru-vault',
  CLOSE: 'tru-close',
  ANALYTICS: 'tru-analytics',

  // Fixed tabs in Workspace
  FIXED_TAB_FOCUS: '__today__',
  FIXED_TAB_HOME: '__home__',
  FIXED_TAB_CLARA_AI_CHAT: '__clara-ai-chat__',

  // Page IDs for home pages
  PAGE_ENGAGEMENT_DASHBOARD: 'engagements-dashboard',
  PAGE_ACTION_TRAY: 'action-tray',
  PAGE_PORTFOLIO: '__portfolio__',

  // Launch page IDs (external apps)
  LAUNCH_CENTER_AUDIT_SOLUTIONS: 'center-audit-solutions',
  LAUNCH_AUDIT_TECH_NAVIGATOR: 'audit-tech-navigator',
  LAUNCH_AUDIT_REPORTS_TOOL: 'audit-reports-tool',
  LAUNCH_ALEX: 'alex',

  // Header modes
  HEADER_MODE_FIRM: true, // Workspace (Portfolio selector)
  HEADER_MODE_ENGAGEMENT: false, // Legacy apps (Engagement selector)

  // Panel names
  PANEL_UTILITY: 'utility',
  PANEL_AI: 'ai',
  PANEL_TEAMS: 'teams',

  // Engagement context parameter
  ENG_PARAM: 'eng',
  APP_PARAM: 'app',
  FRAME_PARAM: 'frame',
  FRAME_LEGACY: 'legacy',

  // Animation timings (milliseconds)
  HEADER_COLLAPSE_DELAY: 2000,
  COLLAPSE_DEBOUNCE: 500,
  PANEL_RESIZE_DEBOUNCE: 100,

  // Layout dimensions (CSS tokens)
  MAIN_HEADER_HEIGHT: 72,
  NAV_HEADER_HEIGHT: 48,
  TOTAL_HEADER_HEIGHT: 120,
  BODY_PADDING: 24,
} as const;

/**
 * CSS CLASS NAME CONSTANTS
 * Prevents hardcoding class names; enables refactoring
 */
export const CLS = {
  // ---- Header & Navigation ----
  MAIN_HEADER: 'main-header',
  NAV_HEADER: 'nav-header',
  NAV_PANEL: 'nav-panel',
  BREADCRUMB: 'breadcrumb',
  BREADCRUMB_ITEM: 'breadcrumb__item',
  BREADCRUMB_CHEVRON: 'breadcrumb__chevron',

  // ---- Buttons & Icons ----
  ICON_BTN: 'icon-btn',
  ICON_BTN_SM: 'icon-btn--sm',
  ICON_BTN_MD: 'icon-btn--md',
  ICON_BTN_LG: 'icon-btn--lg',
  ICON_BTN_ACTIVE: 'icon-btn--active',
  ICON_BTN_DISABLED: 'icon-btn--disabled',

  SORT_BTN: 'sort-btn',
  SORT_BTN_ACTIVE: 'sort-btn--active',

  WAFFLE_BTN: 'waffle-btn',

  // ---- Dropdowns ----
  DROPDOWN: 'dropdown',
  DROPDOWN_OPEN: 'dropdown--open',
  DROPDOWN_TRIGGER: 'dropdown__trigger',
  DROPDOWN_MENU: 'dropdown__menu',
  DROPDOWN_ITEM: 'dropdown__item',
  DROPDOWN_ITEM_ACTIVE: 'dropdown__item--active',

  ENG_DROPDOWN: 'eng-dropdown',
  ENG_DROPDOWN_OPEN: 'eng-dropdown--open',

  // ---- Tiles & Cards ----
  TILE_ICON: 'tile-icon',
  TILE_ICON_SM: 'tile-icon--sm',
  TILE_ICON_MD: 'tile-icon--md',
  TILE_ICON_TEAL: 'tile-icon--teal',
  TILE_ICON_PINK: 'tile-icon--pink',
  TILE_ICON_YELLOW: 'tile-icon--yellow',
  TILE_ICON_PURPLE: 'tile-icon--purple',
  TILE_ICON_GREEN: 'tile-icon--green',
  TILE_ICON_BLUE: 'tile-icon--blue',
  TILE_ICON_CYAN: 'tile-icon--cyan',

  BODY_CARD: 'body-card',
  BODY_CARD_GRID: 'body-card-grid',
  BODY_CARD_GRID_CARD: 'body-card-grid__card',
  BODY_CARD_GRID_CARD_ICON: 'body-card-grid__card-icon',

  // ---- Badges & Progress ----
  BADGE: 'badge',
  PROGRESS_BAR: 'progress-bar',
  PROGRESS_BAR_SM: 'progress-bar--sm',
  PROGRESS_BAR_MD: 'progress-bar--md',

  // ---- Avatar ----
  AVATAR: 'avatar',
  AVATAR_SM: 'avatar--sm',
  AVATAR_MD: 'avatar--md',
  AVATAR_LG: 'avatar--lg',
  AVATAR_PLACEHOLDER: 'avatar--placeholder',

  // ---- Search & Filter ----
  SEARCH_INPUT: 'search-input',
  SEARCH_FILTER_BAR: 'search-filter-bar',

  // ---- Panels ----
  UTILITY_PANEL: 'utility-panel',
  UTILITY_PANEL_OPEN: 'utility-panel--open',
  UTILITY_PANEL_PINNED: 'utility-panel--pinned',

  AI_PANEL: 'ai-panel',
  AI_PANEL_OPEN: 'ai-panel--open',

  TEAMS_PANEL: 'teams-panel',
  TEAMS_PANEL_OPEN: 'teams-panel--open',

  // ---- Body Content ----
  BODY_CONTENT: 'body-content',
  BODY_CONTENT_WRAP: 'body-content-wrap',
  APP_FRAME: 'app-frame',

  // ---- Task Detail (KCi) ----
  TASK_DETAIL: 'task-detail',
  TASK_DETAIL_SECTION: 'task-detail__section',
  TASK_DETAIL_SECTION_MUTED: 'task-detail__section--muted',
  TASK_DETAIL_SECTION_HEADER: 'task-detail__section-header',

  // ---- Overlay & Hidden ----
  HIDDEN: 'hidden',
  VISIBLE: 'visible',
  OVERLAY: 'overlay',

  // ---- State Classes ----
  DISABLED: 'disabled',
  ACTIVE: 'active',
  SELECTED: 'selected',
  HOVER: 'hover',
  FOCUS: 'focus',

  // ---- Layout Modifiers ----
  HAS_CHAT_BAR: '--has-chat-bar',
  PANEL_OPEN: '--panel-open',
  SIDE_BY_SIDE: '--side-by-side',
} as const;

/**
 * MODIFIERS FOR CONDITIONAL CLASS NAMES
 */
export const MOD = {
  ACTIVE: 'active',
  DISABLED: 'disabled',
  SELECTED: 'selected',
  HOVER: 'hover',
  OPEN: 'open',
  PINNED: 'pinned',
  EXPANDED: 'expanded',
  COLLAPSED: 'collapsed',
} as const;

/**
 * ICON NAMES (Material Symbols)
 */
export const ICONS = {
  CHEVRON_DOWN: 'expand_more',
  CHEVRON_RIGHT: 'chevron_right',
  CHEVRON_UP: 'expand_less',
  CLOSE: 'close',
  SEARCH: 'search',
  FILTER: 'filter_list',
  SORT: 'sort',
  SETTINGS: 'settings',
  MENU: 'menu',
  MORE_VERT: 'more_vert',
  MORE_HORIZ: 'more_horiz',
  DRAG_HANDLE: 'drag_handle',
  EXPAND: 'open_in_full',
  COLLAPSE: 'close_fullscreen',
  PIN: 'push_pin',
  UNPIN: 'pin_off',
  STAR: 'star',
  STAR_OUTLINE: 'star_outline',
  HOME: 'home',
  TODAY: 'today',
  ADS_CLICK: 'ads_click',
  WORK: 'work',
  DOMAIN: 'domain',
  DONE: 'done',
  ERROR: 'error',
  WARNING: 'warning_amber',
  INFO: 'info',
} as const;

/**
 * COLORS FOR TILES & UI ELEMENTS
 */
export const COLORS = {
  CYAN: 'cyan',
  TEAL: 'teal',
  PINK: 'pink',
  YELLOW: 'yellow',
  PURPLE: 'purple',
  GREEN: 'green',
  BLUE: 'blue',
} as const;

/**
 * AVATAR SIZES
 */
export const AVATAR_SIZES = {
  SM: 20,
  MD: 32,
  LG: 48,
} as const;

/**
 * CHAT HISTORY FLYOUT — Grouping & bucketing configuration
 */
export const CHAT_HISTORY_CONFIG = {
    // Day-boundary thresholds used to bucket sessions by recency
    BUCKET_DAYS_WEEK: 7,
    BUCKET_DAYS_PAST30: 30,
    BUCKET_DAYS_PAST60: 60,
    BUCKET_DAYS_PAST90: 90,

    // Group identifiers
    GROUP_ID_TODAY: 'today',
    GROUP_ID_THIS_WEEK: 'this-week',
    GROUP_ID_PAST_30: 'past-30',
    GROUP_ID_PAST_60: 'past-60',
    GROUP_ID_PAST_90: 'past-90',

    // Group display labels
    GROUP_LABEL_TODAY: "Today's Chats",
    GROUP_LABEL_THIS_WEEK: "This Week's",
    GROUP_LABEL_PAST_30: 'Past 30 Days',
    GROUP_LABEL_PAST_60: 'Past 60 Days',
    GROUP_LABEL_PAST_90: 'Past 90 Days',

    // Group IDs that are collapsed by default in the chat flyout
    DEFAULT_COLLAPSED_GROUP_IDS: ['past-30', 'past-60', 'past-90'] as string[],
} as const;

/**
 * HELPER: Create class name string with modifiers
 * @example makeCls('icon-btn', { active: true, disabled: false })
 */
export function makeCls(base: string, mods?: Record<string, boolean>): string {
  const classes = [base];
  if (mods) {
    Object.entries(mods).forEach(([key, value]) => {
      if (value) {
        classes.push(`${base}--${key}`);
      }
    });
  }
  return classes.join(' ');
}

/**
 * HELPER: Combine multiple class names safely
 * @example combine('base-class', isActive && 'active-class', customClass)
 */
export function combine(...classes: (string | undefined | false)[]): string {
  return classes.filter(Boolean).join(' ');
}
